import { showUserFriendlyNotyf, __ } from "../../utils/notyf-helpers.js";

/**
 * @file Shared API Service Module
 * @description Common API utilities and service functions
 */

export class ApiService {
  constructor() {
    // Prefer localized base URL when available
    this.baseUrl =
      (typeof window !== "undefined" && window.wpRest && window.wpRest.baseUrl) || "/wp-json/wp/v2";
    this.timeout = 10000;
    this.retryAttempts = 3;
    this.retryDelay = 1000;
  }

  /**
   * Make API request with error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      timeout: this.timeout,
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    // Attach REST nonce if provided via localization
    try {
      if (typeof window !== "undefined" && window.wpRest && window.wpRest.nonce) {
        config.headers = {
          ...config.headers,
          "X-WP-Nonce": window.wpRest.nonce,
        };
      }
    } catch (_) {}

    let lastError;

    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        const response = await this.makeRequest(url, config);
        return response;
      } catch (error) {
        lastError = error;

        if (attempt < this.retryAttempts) {
          await this.delay(this.retryDelay * attempt);
        }
      }
    }

    throw lastError;
  }

  /**
   * Make actual HTTP request
   */
  async makeRequest(url, config) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);

    try {
      const response = await fetch(url, {
        ...config,
        credentials: "same-origin",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        data,
        headers: response.headers,
        status: response.status,
      };
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Delay function for retry attempts
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get posts with pagination
   */
  async getPosts(postType = "post", options = {}) {
    const params = new URLSearchParams({
      per_page: options.perPage || 10,
      page: options.page || 1,
      ...options,
    });

    return this.request(`/${postType}?${params.toString()}`);
  }

  /**
   * Get single post by ID
   */
  async getPost(postType, id) {
    return this.request(`/${postType}/${id}`);
  }

  /**
   * Search posts
   */
  async searchPosts(searchTerm, postType = "post", options = {}) {
    const params = new URLSearchParams({
      search: searchTerm,
      per_page: options.perPage || 10,
      page: options.page || 1,
      ...options,
    });

    return this.request(`/${postType}?${params.toString()}`);
  }

  /**
   * Get terms for a taxonomy
   */
  async getTerms(taxonomy, options = {}) {
    const params = new URLSearchParams({
      per_page: options.perPage || 100,
      ...options,
    });

    return this.request(`/${taxonomy}?${params.toString()}`);
  }

  /**
   * Get user data
   */
  async getUser(userId) {
    return this.request(`/users/${userId}`);
  }

  /**
   * Update post
   */
  async updatePost(postType, id, data, token) {
    return this.request(`/${postType}/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Create post
   */
  async createPost(postType, data, token) {
    return this.request(`/${postType}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete post
   */
  async deletePost(postType, id, token) {
    return this.request(`/${postType}/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Upload media
   */
  async uploadMedia(file, token) {
    const formData = new FormData();
    formData.append("file", file);

    return this.request("/media", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });
  }

  /**
   * Get site settings
   */
  async getSiteSettings() {
    return this.request("/settings");
  }

  /**
   * Get site info
   */
  async getSiteInfo() {
    return this.request("/");
  }

  /**
   * Get comments for a post
   */
  async getComments(postId, options = {}) {
    const params = new URLSearchParams({
      post: postId,
      per_page: options.perPage || 10,
      page: options.page || 1,
      ...options,
    });

    return this.request(`/comments?${params.toString()}`);
  }

  /**
   * Create comment
   */
  async createComment(data) {
    return this.request("/comments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * Get custom endpoint data
   */
  async getCustomEndpoint(endpoint, options = {}) {
    const params = new URLSearchParams(options);
    const queryString = params.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url);
  }

  /**
   * Post to custom endpoint
   */
  async postToCustomEndpoint(endpoint, data, token = null) {
    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return this.request(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
  }

  /**
   * Handle API errors
   */
  handleApiError(error, context = "") {
    console.error(`API Error ${context}:`, error);

    let message = __("An error occurred while processing your request.", "iphub");

    if (error.name === "AbortError") {
      message = __("Request timed out. Please try again.", "iphub");
    } else if (error.message.includes("HTTP 401")) {
      message = __(
        "Phiên đăng nhập đã hết hạn hoặc thiếu quyền truy cập. Vui lòng đăng nhập lại.",
        "iphub",
      );
    } else if (error.message.includes("HTTP 403")) {
      message = __("Access denied. You do not have permission to perform this action.", "iphub");
    } else if (error.message.includes("HTTP 404")) {
      message = __("The requested resource was not found.", "iphub");
    } else if (error.message.includes("HTTP 500")) {
      message = __("Server error. Please try again later.", "iphub");
    }

    showUserFriendlyNotyf(message, "error");
    return { success: false, error: error.message };
  }

  /**
   * Validate API response
   */
  validateResponse(response) {
    if (!response || !response.data) {
      throw new Error("Invalid API response format");
    }
    return response;
  }

  /**
   * Parse pagination headers
   */
  parsePaginationHeaders(headers) {
    return {
      total: parseInt(headers.get("X-WP-Total") || 0),
      totalPages: parseInt(headers.get("X-WP-TotalPages") || 0),
      currentPage: parseInt(headers.get("X-WP-CurrentPage") || 1),
    };
  }

  /**
   * Get API statistics
   */
  getApiStats() {
    return {
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      retryAttempts: this.retryAttempts,
      retryDelay: this.retryDelay,
    };
  }
}

/**
 * Create API service instance
 */
export function createApiService(config = {}) {
  const apiService = new ApiService();

  // Apply custom configuration
  if (config.baseUrl) apiService.baseUrl = config.baseUrl;
  if (config.timeout) apiService.timeout = config.timeout;
  if (config.retryAttempts) apiService.retryAttempts = config.retryAttempts;
  if (config.retryDelay) apiService.retryDelay = config.retryDelay;

  return apiService;
}

/**
 * Initialize default API service
 */
export function initApiService() {
  const apiService = createApiService();

  // Store instance globally for access
  window.IPHUB_API_SERVICE = apiService;

  return apiService;
}
